import { combineLatest as observableCombineLatest, Subject, combineLatest } from "rxjs";
import { distinctUntilChanged, map, startWith, switchMap } from "rxjs/operators";
import { Viewer } from "./Viewer";
import { SubscriptionHolder } from "../utils/SubscriptionHolder";
import { STREAMTYPE } from "../internal";
import { Infra3DDataProviderBase } from "../api/Infra3DDataProviderBase";
export class Observer {
    constructor(eventEmitter, navigator, container) {
        this._subscriptions = new SubscriptionHolder();
        this._container = container;
        this._eventEmitter = eventEmitter;
        this._navigator = navigator;
        //this._projection = new Projection();
        this._started = false;
        this._navigable$ = new Subject();
        const subs = this._subscriptions;
        // navigable and loading should always emit, also when cover is activated.
        subs.push(this._navigable$.subscribe((navigable) => {
            this._eventEmitter.fire(Viewer.navigablechanged, undefined, navigable);
        }));
        subs.push(this._navigator.loadingService.loading$.subscribe((loading) => {
            this._eventEmitter.fire(Viewer.loadingchanged, undefined, loading);
        }));
    }
    get started() {
        return this._started;
    }
    get navigable$() {
        return this._navigable$;
    }
    /*
    public get projection(): Projection {
        return this._projection;
    }
    */
    dispose() {
        this.stopEmit();
        this._subscriptions.unsubscribe();
    }
    /*
    public project$(latLon: ILatLon): Observable<number[]> {
        return observableCombineLatest(
            this._container.renderService.renderCamera$,
            this._navigator.stateService.currentNode$,
            this._navigator.stateService.reference$
        ).pipe(
            first(),
            map(([render, node, reference]: [RenderCamera, Node, ILatLonAlt]): number[] => {
                if (this._projection.distanceBetweenLatLons(latLon, node.latLon) > 1000) {
                    return null;
                }

                const canvasPoint: number[] = this._projection.latLonToCanvas(
                    latLon,
                    this._container.container,
                    render,
                    reference
                );

                return canvasPoint ? [Math.round(canvasPoint[0]), Math.round(canvasPoint[1])] : null;
            })
        );
    }*/
    /*
    public projectBasic$(basicPoint: number[]): Observable<number[]> {
        return observableCombineLatest(
            this._container.renderService.renderCamera$,
            this._navigator.stateService.currentTransform$
        ).pipe(
            first(),
            map(([render, transform]: [RenderCamera, Transform]): number[] => {
                const canvasPoint: number[] = this._projection.basicToCanvas(
                    basicPoint,
                    this._container.container,
                    render,
                    transform
                );

                return canvasPoint ? [Math.round(canvasPoint[0]), Math.round(canvasPoint[1])] : null;
            })
        );
    }
    */
    startEmit() {
        if (this._started) {
            return;
        }
        this._started = true;
        this._currentNodeSubscription = this._navigator.stateService.currentNodeExternal$.subscribe((node) => {
            this._eventEmitter.fire(Viewer.nodechanged, undefined, node);
        });
        this._sequenceEdgesSubscription = this._navigator.stateService.currentNodeExternal$
            .pipe(switchMap((node) => {
            return node.sequenceEdges$;
        }))
            .subscribe((status) => {
            this._eventEmitter.fire(Viewer.sequenceedgeschanged, undefined, status);
        });
        this._spatialEdgesSubscription = this._navigator.stateService.currentNodeExternal$
            .pipe(switchMap((node) => {
            return node.spatialEdges$;
        }))
            .subscribe((status) => {
            this._eventEmitter.fire(Viewer.spatialedgeschanged, undefined, status);
        });
        this._moveSubscription = observableCombineLatest(this._navigator.stateService.inMotion$
        //this._container.mouseService.active$,
        //this._container.touchService.active$
        )
            .pipe(map((values) => {
            return values[0] || values[1] || values[2];
        }), distinctUntilChanged())
            .subscribe((started) => {
            if (started) {
                this._eventEmitter.fire(Viewer.movestart, undefined, null);
            }
            else {
                this._eventEmitter.fire(Viewer.moveend, undefined, null);
            }
        });
        this._cameraConfigurationSubscription = this._navigator.cameraService.cameraConfigurations$.subscribe((nodes) => {
            this._eventEmitter.fire(Viewer.cameraconfigurationchanged, undefined, nodes);
        });
        this._campaignSubscription = this._navigator.campaignService.campaigns$.subscribe((campaigns) => {
            this._eventEmitter.fire(Viewer.campaignschanged, undefined, campaigns);
        });
        this._panzoomSubscription = this._container.scene.userinputapi.panzoomchanged$.subscribe((panzoom) => {
            this._eventEmitter.fire(Viewer.panzoomchanged, undefined, panzoom);
        });
        this._panorotationSubscription = this._container.scene.userinputapi.panorotationchanged$.subscribe((panzoom) => {
            this._eventEmitter.fire(Viewer.panorotationchanged, undefined, panzoom);
        });
        this._playingSubscription = this._navigator.playService.playing$.subscribe((playing) => {
            this._eventEmitter.fire(Viewer.playingchanged, undefined, playing);
        });
        this._directionSubscription = this._navigator.playService.direction$.subscribe((direction) => {
            this._eventEmitter.fire(Viewer.directionchanged, undefined, direction);
        });
        this._speedSubscription = this._navigator.playService.speed$.subscribe((speed) => {
            this._eventEmitter.fire(Viewer.speedchanged, undefined, speed);
        });
        if (this._navigator.api.data instanceof Infra3DDataProviderBase) {
            this._collectionSubscription = this._navigator.api.data.collection$
                .pipe(distinctUntilChanged())
                .subscribe((collection) => {
                this._eventEmitter.fire(Viewer.collectionchanged, undefined, collection);
            });
        }
        this._geoframeSubscription = this._container.scene.userinputapi.geoframechanged$.subscribe((geoframe) => {
            this._eventEmitter.fire(Viewer.geoframechanged, undefined, geoframe);
        });
        this._geoframecollectionSubscription = this._container.scene.userinputapi.geoframecollectionchanged$.subscribe((collection) => {
            this._eventEmitter.fire(Viewer.geoframecollectionchanged, undefined, collection);
        });
        this._navigationAxisSubscription = this._container.scene.userinputapi.navigationaxischanged$.subscribe((axis) => {
            this._eventEmitter.fire(Viewer.navigationaxischanged, undefined, axis);
        });
        this._measurementAxisSubscription = this._container.scene.userinputapi.measurementaxischanged$.subscribe((axis) => {
            this._eventEmitter.fire(Viewer.measurementaxischanged, undefined, axis);
        });
        this._bufferingSubscription = this._container.scene.userinputapi.buffering$.subscribe((buffering) => {
            this._eventEmitter.fire(Viewer.buffering, undefined, buffering);
        });
        this._measuremodeSubscription = this._container.scene.userinputapi.measuremodechanged$.subscribe((measuremode) => {
            this._eventEmitter.fire(Viewer.measuremodechanged, undefined, measuremode);
        });
        this._layerChangedSubscription = this._container.scene.userinputapi.layerchanged$.subscribe((layer) => {
            this._eventEmitter.fire(Viewer.layerchanged, undefined, layer);
        });
        this._layerAddedSubscription = this._container.scene.userinputapi.layeradded$.subscribe((layer) => {
            this._eventEmitter.fire(Viewer.layeradded, undefined, layer);
        });
        this._layerRemovedSubscription = this._container.scene.userinputapi.layerremoved$.subscribe((layer) => {
            this._eventEmitter.fire(Viewer.layerremoved, undefined, layer);
        });
        this._layerUpdatedSubscription = this._container.scene.userinputapi.layerupdated$.subscribe(([layer, updates]) => {
            this._eventEmitter.fire(Viewer.layerupdated, undefined, [layer, updates]);
        });
        this._layerSavedSubscription = this._container.scene.userinputapi.layersaved$.subscribe(([layer, inserts, updates, deletions]) => {
            this._eventEmitter.fire(Viewer.layersaved, undefined, [layer, inserts, updates, deletions]);
        });
        this._featureSelectedSubscription = this._container.scene.userinputapi.featureselected$.subscribe(([layer, id]) => {
            this._eventEmitter.fire(Viewer.featureselected, undefined, [layer, id]);
        });
        this._featureDeselectedSubscription = this._container.scene.userinputapi.featuredeselected$.subscribe(([layer, id]) => {
            this._eventEmitter.fire(Viewer.featureselected, undefined, [layer, id]);
        });
        this._lookazimuthSubscription = combineLatest([
            this._container.scene.userinputapi.geoframechanged$,
            this._container.scene.userinputapi.panorotationchanged$.pipe(startWith({
                type: "pano",
                lat: 0,
                lon: 0,
                fov: 0,
            })),
        ])
            .pipe(map(([geoframe, panorotation]) => {
            let _panorotation = 0;
            if (geoframe.getMeasureInfo().streamtype === STREAMTYPE.CALOTTE ||
                geoframe.getMeasureInfo().streamtype === STREAMTYPE.CUBEMAP) {
                _panorotation = panorotation.lon;
            }
            return (geoframe.getHorizontalOrientation() + _panorotation) % 360;
        }), distinctUntilChanged())
            .subscribe((lookazimuth) => {
            this._eventEmitter.fire(Viewer.lookazimuthchanged, undefined, lookazimuth);
        });
        /*
        this._bearingSubscription = this._container.renderService.bearing$
            .pipe(
                auditTime(100),
                distinctUntilChanged((b1: number, b2: number): boolean => {
                    return Math.abs(b2 - b1) < 1;
                })
            )
            .subscribe((bearing): void => {
                this._eventEmitter.fire(Viewer.bearingchanged, bearing);
            });

        const mouseMove$: Observable<MouseEvent> = this._container.mouseService.active$.pipe(
            switchMap(
                (active: boolean): Observable<MouseEvent> => {
                    return active ? observableEmpty() : this._container.mouseService.mouseMove$;
                }
            )
        );
        */
        /*
        this._viewerMouseEventSubscription = observableMerge(
            this._mapMouseEvent$(Viewer.click, this._container.mouseService.staticClick$),
            this._mapMouseEvent$(Viewer.contextmenu, this._container.mouseService.contextMenu$),
            this._mapMouseEvent$(Viewer.dblclick, this._container.mouseService.dblClick$),
            this._mapMouseEvent$(Viewer.mousedown, this._container.mouseService.mouseDown$),
            this._mapMouseEvent$(Viewer.mousemove, mouseMove$),
            this._mapMouseEvent$(Viewer.mouseout, this._container.mouseService.mouseOut$),
            this._mapMouseEvent$(Viewer.mouseover, this._container.mouseService.mouseOver$),
            this._mapMouseEvent$(Viewer.mouseup, this._container.mouseService.mouseUp$)
        )
            .pipe(
                withLatestFrom(
                    this._container.renderService.renderCamera$,
                    this._navigator.stateService.reference$,
                    this._navigator.stateService.currentTransform$
                ),
                map(
                    ([[type, event], render, reference, transform]: [
                        [string, MouseEvent],
                        RenderCamera,
                        ILatLonAlt,
                        Transform
                    ]): IViewerMouseEvent => {
                        const unprojection: IUnprojection = this._projection.eventToUnprojection(
                            event,
                            this._container.container,
                            render,
                            reference,
                            transform
                        );

                        return {
                            basicPoint: unprojection.basicPoint,
                            latLon: unprojection.latLon,
                            originalEvent: event,
                            pixelPoint: unprojection.pixelPoint,
                            target: <Viewer>this._eventEmitter,
                            type: type,
                        };
                    }
                )
            )
            .subscribe((event: IViewerMouseEvent): void => {
                this._eventEmitter.fire(event.type, event);
            });
        */
        /*
        this._positionSubscription = this._container.renderService.renderCamera$
            .pipe(
                distinctUntilChanged(
                    ([x1, y1], [x2, y2]): boolean => {
                        return this._closeTo(x1, x2, 1e-2) && this._closeTo(y1, y2, 1e-2);
                    },
                    (rc: RenderCamera): number[] => {
                        return rc.camera.position.toArray();
                    }
                )
            )
            .subscribe((): void => {
                this._eventEmitter.fire(Viewer.positionchanged, {
                    target: this._eventEmitter,
                    type: Viewer.positionchanged,
                });
            });
        */
        /*
        this._povSubscription = this._container.renderService.renderCamera$
            .pipe(
                distinctUntilChanged(
                    ([phi1, theta1], [phi2, theta2]): boolean => {
                        return this._closeTo(phi1, phi2, 1e-3) && this._closeTo(theta1, theta2, 1e-3);
                    },
                    (rc: RenderCamera): [number, number] => {
                        return [rc.rotation.phi, rc.rotation.theta];
                    }
                )
            )
            .subscribe((): void => {
                this._eventEmitter.fire(Viewer.povchanged, {
                    target: this._eventEmitter,
                    type: Viewer.povchanged,
                });
            });
        */
        /*
        this._fovSubscription = this._container.renderService.renderCamera$
            .pipe(
                distinctUntilChanged(
                    (fov1, fov2): boolean => {
                        return this._closeTo(fov1, fov2, 1e-2);
                    },
                    (rc: RenderCamera): number => {
                        return rc.perspective.fov;
                    }
                )
            )
            .subscribe((): void => {
                this._eventEmitter.fire(Viewer.fovchanged, {
                    target: this._eventEmitter,
                    type: Viewer.fovchanged,
                });
            });
        */
    }
    stopEmit() {
        if (!this.started) {
            return;
        }
        this._started = false;
        //this._bearingSubscription.unsubscribe();
        this._currentNodeSubscription.unsubscribe();
        //this._fovSubscription.unsubscribe();
        this._moveSubscription.unsubscribe();
        //this._positionSubscription.unsubscribe();
        //this._povSubscription.unsubscribe();
        this._sequenceEdgesSubscription.unsubscribe();
        this._spatialEdgesSubscription.unsubscribe();
        this._cameraConfigurationSubscription.unsubscribe();
        this._campaignSubscription.unsubscribe();
        this._panzoomSubscription.unsubscribe();
        this._panorotationSubscription.unsubscribe();
        this._playingSubscription.unsubscribe();
        this._directionSubscription.unsubscribe();
        this._speedSubscription.unsubscribe();
        if (this._collectionSubscription)
            this._collectionSubscription.unsubscribe();
        this._geoframeSubscription.unsubscribe();
        this._geoframecollectionSubscription.unsubscribe();
        this._navigationAxisSubscription.unsubscribe();
        this._measurementAxisSubscription.unsubscribe();
        this._bufferingSubscription.unsubscribe();
        this._measuremodeSubscription.unsubscribe();
        this._layerChangedSubscription.unsubscribe();
        this._layerAddedSubscription.unsubscribe();
        this._layerRemovedSubscription.unsubscribe();
        this._layerUpdatedSubscription.unsubscribe();
        this._layerSavedSubscription.unsubscribe();
        this._featureSelectedSubscription.unsubscribe();
        this._featureDeselectedSubscription.unsubscribe();
        this._lookazimuthSubscription.unsubscribe();
        //this._viewerMouseEventSubscription.unsubscribe();
        this._bearingSubscription = null;
        this._currentNodeSubscription = null;
        this._fovSubscription = null;
        this._moveSubscription = null;
        this._positionSubscription = null;
        this._povSubscription = null;
        this._sequenceEdgesSubscription = null;
        this._spatialEdgesSubscription = null;
        this._viewerMouseEventSubscription = null;
        this._cameraConfigurationSubscription = null;
        this._campaignSubscription = null;
        this._panzoomSubscription = null;
        this._panorotationSubscription = null;
        this._playingSubscription = null;
        this._directionSubscription = null;
        this._speedSubscription = null;
        this._collectionSubscription = null;
        this._geoframeSubscription = null;
        this._geoframecollectionSubscription = null;
        this._navigationAxisSubscription = null;
        this._measurementAxisSubscription = null;
        this._bufferingSubscription = null;
        this._measuremodeSubscription = null;
        this._layerChangedSubscription = null;
        this._layerAddedSubscription = null;
        this._layerRemovedSubscription = null;
        this._layerUpdatedSubscription = null;
        this._layerSavedSubscription = null;
        this._featureSelectedSubscription = null;
        this._featureDeselectedSubscription = null;
        this._lookazimuthSubscription = null;
    }
    /*
    public unproject$(canvasPoint: number[]): Observable<ILatLon> {
        return observableCombineLatest(
            this._container.renderService.renderCamera$,
            this._navigator.stateService.reference$,
            this._navigator.stateService.currentTransform$
        ).pipe(
            first(),
            map(
                ([render, reference, transform]: [RenderCamera, ILatLonAlt, Transform]): ILatLon => {
                    const unprojection: IUnprojection = this._projection.canvasToUnprojection(
                        canvasPoint,
                        this._container.container,
                        render,
                        reference,
                        transform
                    );

                    return unprojection.latLon;
                }
            )
        );
    }
    */
    /*
    public unprojectBasic$(canvasPoint: number[]): Observable<number[]> {
        return observableCombineLatest(
            this._container.renderService.renderCamera$,
            this._navigator.stateService.currentTransform$
        ).pipe(
            first(),
            map(([render, transform]: [RenderCamera, Transform]): number[] => {
                return this._projection.canvasToBasic(canvasPoint, this._container.container, render, transform);
            })
        );
    }
    */
    _closeTo(v1, v2, absoluteTolerance) {
        return Math.abs(v1 - v2) <= absoluteTolerance;
    }
    _mapMouseEvent$(type, mouseEvent$) {
        return mouseEvent$.pipe(map((event) => {
            return [type, event];
        }));
    }
}
//# sourceMappingURL=Observer.js.map